import {
    InputLabel,
    FormControl,
    Select,
    MenuItem,
    FormHelperText,
    Checkbox,
    ListItemText,
    IconButton,
} from '@mui/material';
import { Controller } from 'react-hook-form';
import ClearIcon from '@mui/icons-material/Clear';
import NoOptionsItem from '../UI/NoOptionsItem';
import { ControllerRenderProps } from 'react-hook-form/dist/types/controller';
import { useCallback, useMemo } from 'react';

export type ReactHookFormSelectOption = {
    id: number;
    [key: string]: any;
};

export type ReactHookFormSelectProps<T extends ReactHookFormSelectOption> = {
    name: string;
    label: string;
    control: any;
    options: T[];
    defaultValue: any;
    mainProp: string;
    secondProp?: string;
    rules: any;
    formErrors: any;
    isMultiSelect: boolean;
    valueProp?: string;
    onItemClick?: () => void;
    showClearButton?: boolean;
    sortByProps?: boolean;
};

function ReactHookFormSelect<T extends ReactHookFormSelectOption>({
    name,
    label,
    control,
    options,
    defaultValue,
    mainProp,
    secondProp,
    rules,
    formErrors,
    isMultiSelect,
    valueProp,
    onItemClick,
    showClearButton,
    sortByProps = true,
    ...props
}: ReactHookFormSelectProps<T>) {
    const labelId = `${name}-label`;
    const valueProperty = valueProp ? valueProp : 'id';

    const sortedOptions = useMemo(() => {
        return sortByProps
            ? [...options].sort((a, b) => {
                  if (a.id === -1 || a[mainProp] === 'Alle') return -1;
                  if (b.id === -1 || b[mainProp] === 'Alle') return 1;

                  const aString = `${a[mainProp]}${secondProp ? ` - ${a[secondProp]}` : ''}`;
                  const bString = `${b[mainProp]}${secondProp ? ` - ${b[secondProp]}` : ''}`;

                  return aString.localeCompare(bString);
              })
            : options;
    }, [mainProp, options, secondProp, sortByProps]);

    const getRenderValue = useCallback(
        (selectedValue: T[]) => {
            selectedValue = Array.isArray(selectedValue) ? selectedValue : [selectedValue];

            const value =
                options.length > 0
                    ? selectedValue
                          .map((value) => {
                              const option = options.find((option) => option[valueProperty] === value);
                              return option ? `${option[mainProp]}${secondProp ? ` - ${option[secondProp]}` : ''}` : '';
                          })
                          .join(', ')
                    : '';

            return <span className="notranslate">{value}</span>;
        },
        [mainProp, options, secondProp, valueProperty]
    );

    const handleMenuItemClick = (field: ControllerRenderProps, value: any) => {
        if (onItemClick) {
            field.onChange(value);
            onItemClick();
        }
    };

    const handleClearClick = (field: ControllerRenderProps) => {
        field.onChange('');
    };

    return (
        <FormControl {...props} fullWidth={true}>
            <InputLabel id={labelId}>{label}</InputLabel>
            <Controller
                name={name}
                control={control}
                defaultValue={defaultValue}
                rules={rules}
                render={({ field, fieldState }) => (
                    <>
                        <Select
                            {...field}
                            label={label}
                            multiple={isMultiSelect}
                            renderValue={(selected) => getRenderValue(selected)}
                            endAdornment={
                                showClearButton ? (
                                    <IconButton
                                        sx={{ display: field.value ? '' : 'none' }}
                                        onClick={() => handleClearClick(field)}
                                    >
                                        <ClearIcon />
                                    </IconButton>
                                ) : null
                            }
                            sx={{
                                '& .MuiSelect-iconOutlined': { display: showClearButton && field.value ? 'none' : '' },
                            }}
                        >
                            {sortedOptions.length === 0 && <NoOptionsItem />}
                            {sortedOptions?.map((option) => (
                                <MenuItem
                                    key={option[valueProperty]}
                                    value={option[valueProperty]}
                                    onMouseDown={(e) => handleMenuItemClick(field, option[valueProperty])}
                                >
                                    {isMultiSelect && <Checkbox checked={field.value.indexOf(option.id) > -1} />}
                                    <ListItemText
                                        primary={`${option[mainProp]} ${secondProp ? ` - ${option[secondProp]}` : ''}`}
                                        className="notranslate"
                                    />
                                </MenuItem>
                            ))}
                        </Select>
                        {fieldState?.error && (
                            <FormHelperText
                                sx={{
                                    color: 'var(--error) !important',
                                }}
                            >
                                {formErrors[name].message}
                            </FormHelperText>
                        )}
                    </>
                )}
            />
        </FormControl>
    );
}

export default ReactHookFormSelect;
